<script setup lang="ts">
/**
 * Replaces every page (except /setup and /account) while the signed-in user
 * has not accepted the current agreement. Driven by eula_acceptance_required
 * from /auth/me. The gateway enforces the same rule on starting an engrave,
 * so this wall is the explanation, not the protection.
 */
import { onMounted, ref } from 'vue'
import { fetchCurrentEula, postEulaAcceptance, type CurrentEula } from '@/api/setup'
import { useAuthStore } from '@/stores/auth'
import EulaDocument from './EulaDocument.vue'

const auth = useAuthStore()

const eula = ref<CurrentEula | null>(null)
const loadError = ref<string | null>(null)
const submitting = ref(false)
const submitError = ref<string | null>(null)

async function load() {
  loadError.value = null
  try {
    eula.value = await fetchCurrentEula()
  } catch (e: any) {
    loadError.value = e?.message || 'Could not load the agreement.'
  }
}

async function accept() {
  if (!eula.value) return
  submitting.value = true
  submitError.value = null
  try {
    await postEulaAcceptance({ eula_version: eula.value.version, eula_sha256: eula.value.sha256 })
    await auth.fetchMe()
  } catch (e: any) {
    // 409 means the text changed while this page was open: show the new one.
    submitError.value = e?.message || 'Could not record your acceptance. Try again.'
    await load()
  } finally {
    submitting.value = false
  }
}

onMounted(load)
</script>

<template>
  <div class="max-w-3xl mx-auto">
    <div class="bg-amber-50 border border-amber-300 rounded-lg p-4 mb-4">
      <p class="font-semibold text-amber-900">Our agreement has changed.</p>
      <p class="text-sm text-amber-800 mt-1">
        You must read and accept it to continue using Keepsake. If you do not
        agree, you can still reach your
        <RouterLink to="/account" class="underline">account</RouterLink>
        or contact <a href="mailto:hello@vetware.io" class="underline">hello@vetware.io</a>.
      </p>
    </div>

    <div v-if="loadError" class="bg-red-50 border border-red-200 rounded p-3">
      <p class="text-red-700 text-sm">{{ loadError }}</p>
      <button @click="load" class="mt-2 text-sm underline text-red-800">Retry</button>
    </div>
    <p v-else-if="!eula" class="text-sm text-gray-500 py-8 text-center">Loading agreement…</p>
    <template v-else>
      <EulaDocument
        :eula="eula"
        :submitting="submitting"
        @accept="accept"
      />
      <p v-if="submitError" class="text-sm text-red-600 mt-2">{{ submitError }}</p>
    </template>
  </div>
</template>
